// UTM capture and attribution utility.
//
// Reads UTM parameters from the URL on first load, persists first-touch and
// last-touch attribution to localStorage, and provides a getUtm() helper that
// merges both into a single attribution object. Never throws — all storage
// access is wrapped in try/catch for SSR / private-browsing safety.

const FIRST_TOUCH_KEY = 'cleveroo_utm_first';
const LAST_TOUCH_KEY = 'cleveroo_utm_last';
const REF_KEY = 'cleveroo_ref';

export interface UtmData {
  source: string | null;
  medium: string | null;
  campaign: string | null;
  term: string | null;
  content: string | null;
  /** The path where UTM params were first seen. */
  landing_page: string | null;
  /** document.referrer at first capture. */
  referrer: string | null;
}

function readFromStorage(key: string): UtmData | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as UtmData;
  } catch {
    return null;
  }
}

function writeToStorage(key: string, data: UtmData): void {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch {
    // Ignore — storage quota or private mode.
  }
}

function parseUtmFromUrl(): UtmData | null {
  try {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    const source = params.get('utm_source');
    const medium = params.get('utm_medium');
    const campaign = params.get('utm_campaign');
    const term = params.get('utm_term');
    const content = params.get('utm_content');
    // Only capture when at least one UTM param is present.
    if (!source && !medium && !campaign && !term && !content) return null;
    return {
      source,
      medium,
      campaign,
      term,
      content,
      landing_page: window.location.pathname,
      referrer: document.referrer || null,
    };
  } catch {
    return null;
  }
}

/**
 * Called once on app mount. Captures the `ref` query parameter from the
 * current URL and persists it as a first-touch referral code to localStorage.
 * Only written once — subsequent page loads with a different `ref` are ignored
 * so attribution always reflects the original referrer. Never throws.
 */
export function captureRef(): void {
  try {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (!ref) return;
    // First-touch only — don't overwrite if already stored.
    if (!localStorage.getItem(REF_KEY)) {
      localStorage.setItem(REF_KEY, ref);
    }
  } catch {
    // Never throw.
  }
}

/**
 * Returns the stored partner referral code, or null if none was captured.
 * Never throws.
 */
export function getRef(): string | null {
  try {
    return localStorage.getItem(REF_KEY);
  } catch {
    return null;
  }
}

/**
 * Called once on app mount. Captures UTM params from the current URL:
 * - First-touch: written to localStorage only if the key is absent.
 * - Last-touch: overwritten whenever UTM params are present.
 */
export function captureUtm(): void {
  try {
    const utmFromUrl = parseUtmFromUrl();
    if (!utmFromUrl) return;
    // First-touch: only set once.
    if (!readFromStorage(FIRST_TOUCH_KEY)) {
      writeToStorage(FIRST_TOUCH_KEY, utmFromUrl);
    }
    // Last-touch: always overwrite when params are present.
    writeToStorage(LAST_TOUCH_KEY, utmFromUrl);
  } catch {
    // Never throw.
  }
}

/**
 * Returns the merged UTM attribution object for the current visitor.
 * Prefers last-touch if available, falls back to first-touch.
 */
export function getUtm(): UtmData {
  const empty: UtmData = {
    source: null,
    medium: null,
    campaign: null,
    term: null,
    content: null,
    landing_page: null,
    referrer: null,
  };
  try {
    const last = readFromStorage(LAST_TOUCH_KEY);
    const first = readFromStorage(FIRST_TOUCH_KEY);
    const base = last ?? first ?? empty;
    // landing_page/referrer always come from first-touch (where the user arrived).
    return {
      ...base,
      landing_page: first?.landing_page ?? base.landing_page,
      referrer: first?.referrer ?? base.referrer,
    };
  } catch {
    return empty;
  }
}
